/* eslint-disable @typescript-eslint/no-explicit-any */
/**
 * Recursively calculates the total number of elements in an array,
 * including nested arrays. Objects are ignored.
 * @param input Array
 */
export const getTotalLength = (input: any[]): number => {
  if (Array.isArray(input)) {
    return input.reduce((total, item) => {
      if (Array.isArray(item)) {
        return total + getTotalLength(item);
      }
      return total + 1;
    }, 0);
  }

  return 0;
};

export type ComboBoxItem = {
  id: string;
  name: string;
  isCustom?: boolean;
};

/**
 * Splits an array of items into two arrays: one containing the ids of the items
 * and the other containing the names of the items.
 * @param items
 */
export const splitArray = (items: ComboBoxItem[]) => {
  const ids: string[] = [];
  const names: string[] = [];

  items.forEach((item) => {
    if (item.isCustom) {
      names.push(item.name);
    } else {
      ids.push(item.id);
    }
  });

  return { ids, names };
};

/**
 * Checks if all the values in the filter object are empty or false.
 * @param filter
 */
export const isEmptyOrFalse = (filter: any) =>
  Object.entries(filter).every(([, value]) => (Array.isArray(value) ? value.length === 0 : value === false));

/**
 * Checks if the provided string contains only numbers (integer or decimal) or is empty.
 *
 * @param value - The string to validate.
 * @param disallowNegative - Whether to disallow negative numbers.
 * @returns `true` if valid, `false` otherwise.
 */
export const containsOnlyNumbers = (value: string, disallowNegative = false): boolean => {
  const pattern = disallowNegative ? /^\d+(\.\d+)?$/ : /^-?\d+(\.\d+)?$/;
  return pattern.test(value) || value === "";
};

/**
 * Counts how many values in the given object are valid numbers (integer or decimal).
 *
 * @param obj - The object containing values to validate.
 * @param disallowNegative - Whether to disallow negative numbers.
 * @returns The count of valid positive number strings.
 */
export const countValidNumbers = (obj: Record<string, any>, disallowNegative = false): number => {
  const pattern = disallowNegative ? /^\d+(\.\d+)?$/ : /^-?\d+(\.\d+)?$/;
  return Object.values(obj).filter((value) => pattern.test(value as string)).length;
};

/**
 * Returns the href for a given type.
 *
 * @param type
 * @param href
 */
export const getHrefByType = (type: "website" | "phone" | "email", href: string) => {
  const protocol = href.startsWith("http://") || href.startsWith("https://") ? "" : `https://`;
  const prefix = type === "phone" ? "tel:" : type === "email" ? "mailto:" : protocol;
  return `${prefix}${href}`;
};

/**
 * Capitalizes the first letter of a string.
 * @param str
 */
export const capitalizeFirstLetter = (str: string): string => {
  if (!str) return str;
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
};
